import { useNodes, useReactFlow, useViewport } from '@xyflow/react';
import { Minus as ZoomMinusIcon } from '@sweep-io/sweep-design/dist/icons';
import { Plus as ZoomPlusIcon } from '@sweep-io/sweep-design/dist/icons';
import { Typography, Button, colors, IconButton, ToggleGroup } from '@sweep-io/sweep-design';
import { Box, Divider, styled } from '@mui/material';
import { VerticalCenteredBox } from '../../pages/environments/styledComponents';
import { useSweepCanvasFitView } from '../useSweepCanvasFitView';

import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import { CanvasMode } from '../canvas-types';
import { SweepCanvasSelectionMode, useCanvasControlsCtx } from './CanvasControlsCtx';
import { useState } from 'react';
import { useForwardPinchToSweepCanvas } from '../hooks/useForwardPinchToSweepCanvas';
import { useKeyboardSelectionMode } from '../hooks/useKeyboardSelectionMode';
import { CSS_Z_INDEX_CANVAS_CONTROLS } from '../const/zIndex';

export interface SweepCanvasControlsProps {
  noFitView?: boolean;
  controlsRightMargin?: number;
}

const StyledBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  minHeight: '100%',
  backgroundColor: colors.white,
  padding: '4px 4px',
  border: `1px solid ${colors.grey[300]}`,
  borderRadius: '6px',
  boxShadow: '0px 2px 25px rgba(0, 0, 0, 0.1)',
  justifyContent: 'space-between',
});

const KeySpan = styled(Box)({
  display: 'inline-block',
  width: '15px',
  height: '15px',
  border: `1px solid ${colors.grey[200]}`,
  borderRadius: '0.5px',
  textAlign: 'center',
});

const toggleOptions = [
  {
    value: SweepCanvasSelectionMode.panOnDrag,
    startIconName: 'CursorDrag',
    tooltip: (
      <>
        Hand <KeySpan>H</KeySpan>
      </>
    ),
  },
  {
    value: SweepCanvasSelectionMode.selectOnDrag,
    startIconName: 'Cursor',
    tooltip: (
      <>
        Select <KeySpan>V</KeySpan>
      </>
    ),
  },
];

export const CanvasControls = ({ disableMultiSelection }: { disableMultiSelection?: boolean }) => {
  const {
    controlsRightMargin = 0,
    noFitView,
    canvasMode = CanvasMode.DEFAULT,
  } = useSweepCanvasPropsCtx();
  const [canvasControlsRef, setCanvasControlsRef] = useState<HTMLDivElement | null>(null);
  const isPreview2 = canvasMode === CanvasMode.PREVIEW2;

  const reactFlowInstance = useReactFlow();
  const { zoom } = useViewport();
  const { zoomTo } = reactFlowInstance;
  const { fitView } = useSweepCanvasFitView();
  const nodes = useNodes();
  const disableFitViewButton = nodes.length === 0;
  const duration = 400;

  const { selectionMode: mode, setSelectionMode: setMode } = useCanvasControlsCtx();
  const { selectionMode } = useKeyboardSelectionMode({
    selectionMode: mode,
    setSelectionMode: setMode,
    disableMultiSelection,
  });

  useForwardPinchToSweepCanvas({ element: canvasControlsRef });

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: isPreview2 ? '8px' : '16px',
        right: (isPreview2 ? 8 : 16) + controlsRightMargin + 'px',
        zIndex: CSS_Z_INDEX_CANVAS_CONTROLS,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
      }}
      ref={setCanvasControlsRef}
    >
      {!disableMultiSelection && (
        <StyledBox gap={1}>
          <ToggleGroup
            value={selectionMode}
            size="medium"
            variant="squared"
            options={toggleOptions}
            onChange={(value) => {
              setMode(value as SweepCanvasSelectionMode);
            }}
          />
        </StyledBox>
      )}
      <StyledBox
        width={noFitView ? '105px' : '212px'}
        sx={{ padding: '0 12px', minHeight: '42px' }}
      >
        <VerticalCenteredBox sx={{ gap: 1 }}>
          <Typography color={colors.grey[800]} variant="body">
            {Math.round(zoom * 100)}%
          </Typography>

          <Box display="flex" justifyContent="center">
            <Box component="span" data-testid="zoom-out-button">
              <IconButton
                variant="flat"
                size="tiny"
                onClick={() => {
                  if (zoom <= 0.25) {
                    zoomTo(0.1, { duration });
                  } else {
                    zoomTo(zoom - 0.25, { duration });
                  }
                }}
              >
                <ZoomMinusIcon size="medium" />
              </IconButton>
            </Box>

            <IconButton
              variant="flat"
              size="tiny"
              onClick={() => {
                if (zoom <= 0.1) {
                  zoomTo(0.25, { duration });
                } else {
                  zoomTo(zoom + 0.25, { duration });
                }
              }}
              data-testid="zoom-in-button"
            >
              <ZoomPlusIcon size="medium" />
            </IconButton>
          </Box>
        </VerticalCenteredBox>

        {!noFitView && (
          <>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: '3px 4px' }} />

            <Box component="span" data-testid="fit-view-button">
              <Button
                variant="flat-grey"
                size="small"
                startIconName="Minimize"
                disabled={disableFitViewButton}
                onClick={() => {
                  fitView({ duration: 400 });
                }}
              >
                Fit View
              </Button>
            </Box>
          </>
        )}
      </StyledBox>
    </Box>
  );
};
